/**
 * Frontend audit recorder.
 *
 * Invokes the admin-gated `audit-capture` edge function to persist audit
 * rows for FE-initiated actions. Server-side mutations emit their own
 * audit rows directly via `_shared/audit.ts` — do not use this from FE
 * code that already goes through an edge function.
 *
 * Failures are swallowed (logged to console) so an audit outage never
 * blocks the originating admin action.
 */

import { supabase } from '@/integrations/supabase/client';

export type FrontendAuditAction =
  | 'impersonation.start'
  | 'impersonation.exit'
  | 'client.status_change'
  | 'role.change'
  | 'admin.remove';

export async function recordAudit(
  action: FrontendAuditAction,
  target: { table: string; id: string },
  changes?: Record<string, unknown>,
): Promise<void> {
  try {
    await supabase.functions.invoke('audit-capture', {
      body: { action, target, changes },
    });
  } catch (err) {
    // Audit failures must never break the originating admin action.
    console.error('[audit-client] capture failed; continuing', err);
  }
}

export const recordImpersonationAudit = (
  kind: 'start' | 'exit',
  clientId: string,
  extra?: Record<string, unknown>,
) =>
  recordAudit(
    `impersonation.${kind}` as FrontendAuditAction,
    { table: 'clients', id: clientId },
    extra,
  );
